import React, { useState, useEffect } from 'react';
import './AdminSettings.css';
import apiClient from '../utils/apiClient';

const AdminSettings = () => {
  // 설정 탭 상태 관리
  const [activeTab, setActiveTab] = useState('basic'); // 'basic' | 'customerType' | 'content'
  
  // 기본설정 상태
  const [basicSettings, setBasicSettings] = useState({
    noticeMessage: '',
    alimtalkEnabled: false,
    alimtalkId: '',
    alimtalkSendNumber: '',
    alimtalkApiKey: '',
    alimtalkSenderKey: ''
  });
  const [isSavingBasic, setIsSavingBasic] = useState(false);

  // 고객 타입 설정 상태
  const [customerTypes, setCustomerTypes] = useState([]);
  const [isLoadingCustomerTypes, setIsLoadingCustomerTypes] = useState(false);
  const [contentTypeOptions, setContentTypeOptions] = useState([]);
  const [isLoadingContentTypes, setIsLoadingContentTypes] = useState(false);
  const [newCustomerType, setNewCustomerType] = useState({ 
    id: '', 
    name: '', 
    group: 'basic',
    contentTypes: [] 
  });
  const [isSavingCustomerType, setIsSavingCustomerType] = useState(false);

  // 고객 타입 수정 모달 상태
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCustomerType, setEditingCustomerType] = useState({
    id: '',
    name: '',
    group: 'basic',
    contentTypes: []
  });
  const [isUpdatingCustomerType, setIsUpdatingCustomerType] = useState(false);

  // 드래그앤드롭 상태 (타입 리스트 순서 변경용)
  const [draggedTypeItem, setDraggedTypeItem] = useState(null);
  const [dragOverTypeItem, setDragOverTypeItem] = useState(null);
  const [dragGroup, setDragGroup] = useState(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // 고객 컨텐츠 설정 상태
  const [contentSettings, setContentSettings] = useState([]);
  const [newContentSetting, setNewContentSetting] = useState({
    id: '',
    icon: '',
    label: '',
    contentType: 'image',
    description: ''
  });
  const [isSavingContent, setIsSavingContent] = useState(false);

  // 고객 컨텐츠 타입 수정 모달 상태
  const [isEditContentModalOpen, setIsEditContentModalOpen] = useState(false);
  const [editingContentSetting, setEditingContentSetting] = useState({
    id: '',
    icon: '',
    label: '',
    contentType: '',
    description: ''
  });
  const [isUpdatingContentSetting, setIsUpdatingContentSetting] = useState(false);

  // 기본설정 로드
  useEffect(() => {
    if (activeTab === 'basic') {
      loadBasicSettings();
    } else if (activeTab === 'customerType') {
      loadCustomerTypes();
      loadContentTypeOptions();
    } else if (activeTab === 'content') {
      loadContentSettings();
    }
  }, [activeTab]);

  // 기본설정 로드 함수
  const loadBasicSettings = async () => {
    try {
      // TODO: API 엔드포인트로 변경
      const response = await apiClient.get('/Customer/GetDefaultConfig');
      if (response.success) {
        setBasicSettings({
          noticeMessage: response.data.notice || '',
          alimtalkEnabled: response.data.useAlimtalk || false,
          alimtalkId: response.data.alimtalkId || '',
          alimtalkSendNumber: response.data.alimtalkSender || '',
          alimtalkApiKey: response.data.alimtalkApi || '',
          alimtalkSenderKey: response.data.senderKey || ''
        });
      } else {
        console.error('기본설정 로드 실패:', response.message);
      }
    } catch (error) {
      console.error('기본설정 로드 실패:', error);
    }
  };

  // 고객 타입 로드 함수
  const loadCustomerTypes = async () => {
    setIsLoadingCustomerTypes(true);
    
    try {
      const response = await apiClient.get('/Customer/GetReportType');
      
      if (response && response.data) {
        // API 응답 데이터를 컴포넌트에서 사용하는 형태로 변환
        const transformedData = response.data.map(item => ({
          idx: item.idx,
          id: item.id,
          name: item.name || item.title,
          group: item.group || 'basic',
          contentTypes: item.menus || item.types || []
        }));
        
        setCustomerTypes(transformedData);
      } else {
        console.warn('고객 타입 데이터가 비어있습니다.');
        setCustomerTypes([]);
      }
    } catch (error) {
      console.error('고객 타입 로드 실패:', error);
      
      // 오류 발생 시 사용자에게 알림
      alert('고객 타입 데이터를 불러오는데 실패했습니다.');
      
      // 실패 시 빈 배열로 설정
      setCustomerTypes([]);
    } finally {
      setIsLoadingCustomerTypes(false);
    }
  };

  // 컨텐츠 타입 옵션 로드 함수
  const loadContentTypeOptions = async () => {
    setIsLoadingContentTypes(true);
    
    try {
      const response = await apiClient.get('/Customer/GetContentType');
      
      if (response && response.data) {
        // API 응답 데이터를 컴포넌트에서 사용하는 형태로 변환
        const transformedOptions = response.data.map(item => ({
          id: item.id || item.idx,
          name: item.name || item.title || item.label
        }));
        
        setContentTypeOptions(transformedOptions);
      } else {
        console.warn('컨텐츠 타입 옵션 데이터가 비어있습니다.');
        setContentTypeOptions([]);
      }
    } catch (error) {
      console.error('컨텐츠 타입 옵션 로드 실패:', error);
      
      // 오류 발생 시 사용자에게 알림
      alert('컨텐츠 타입 옵션을 불러오는데 실패했습니다.');
      
      // 실패 시 빈 배열로 설정
      setContentTypeOptions([]);
    } finally {
      setIsLoadingContentTypes(false);
    }
  };

  // 컨텐츠 설정 로드 함수
  const loadContentSettings = async () => {
    try {
      // TODO: API 엔드포인트로 변경
      const response = await apiClient.get('/Customer/GetContentType');
      setContentSettings(response.data);
      
      // 임시 데이터
      // setContentSettings([
      //   {
      //     id: 'homepage',
      //     icon: '<svg>...</svg>',
      //     label: '홈페이지',
      //     contentType: 'page'
      //   },
      //   {
      //     id: 'blog',
      //     icon: '<svg>...</svg>',
      //     label: '블로그',
      //     contentType: 'post'
      //   }
      // ]);
    } catch (error) {
      console.error('컨텐츠 설정 로드 실패:', error);
    }
  };

  // 기본설정 저장
  const handleSaveBasicSettings = async () => {
    if (!basicSettings.noticeMessage.trim()) {
      alert('공지사항 문구를 입력해주세요.');
      return;
    }

    // 알림톡 사용 시 필수 필드 검증
    if (basicSettings.alimtalkEnabled) {
      if (!basicSettings.alimtalkId.trim()) {
        alert('알림톡 아이디를 입력해주세요.');
        return;
      }
      if (!basicSettings.alimtalkSendNumber.trim()) {
        alert('알림톡 발송번호를 입력해주세요.');
        return;
      }
      if (!basicSettings.alimtalkApiKey.trim()) {
        alert('알림톡 API Key를 입력해주세요.');
        return;
      }
      if (!basicSettings.alimtalkSenderKey.trim()) {
        alert('알림톡 Sender Key를 입력해주세요.');
        return;
      }
    }

    setIsSavingBasic(true);
    try {
      // TODO: API 엔드포인트로 변경
      const response = await apiClient.post('/Customer/UpdateDefaultConfig', {
        notice: basicSettings.noticeMessage,
        useAlimtalk: basicSettings.alimtalkEnabled,
        alimtalkId: basicSettings.alimtalkId,
        alimtalkSender: basicSettings.alimtalkSendNumber,
        alimtalkApi: basicSettings.alimtalkApiKey,
        alimtalkSenderKey: basicSettings.alimtalkSenderKey
      });
      if (response.success) {
        alert('기본설정이 저장되었습니다.');
      } else {
        alert(response.message);
      }
      
    } catch (error) {
      console.error('기본설정 저장 실패:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSavingBasic(false);
    }
  };

  // 고객 타입 추가
  const handleAddCustomerType = async () => {
    if (!newCustomerType.id.trim() || !newCustomerType.name.trim()) {
      alert('ID와 이름을 모두 입력해주세요.');
      return;
    }

    if (newCustomerType.contentTypes.length === 0) {
      alert('최소 하나의 컨텐츠 타입을 선택해주세요.');
      return;
    }

    // 중복 체크
    if (customerTypes.some(type => type.id === newCustomerType.id)) {
      alert('이미 존재하는 ID입니다.');
      return;
    }

    setIsSavingCustomerType(true);
    try {
      // TODO: API 엔드포인트로 변경
      const response = await apiClient.post('/Customer/AddCustomerType', newCustomerType);
      if (response.success) {
        setCustomerTypes([...customerTypes, { ...newCustomerType }]);
        setNewCustomerType({ id: '', name: '', contentTypes: [] });
        await loadCustomerTypes();
        alert('고객 타입이 추가되었습니다.');
      } else {
        alert(response.message);
      }
    } catch (error) {
      console.error('고객 타입 추가 실패:', error);
      alert('추가에 실패했습니다.');
    } finally {
      setIsSavingCustomerType(false);
    }
  };

  // 컨텐츠 타입 체크박스 핸들러 (새 고객 타입용)
  const handleContentTypeChange = (contentTypeId, checked) => {
    setNewCustomerType(prev => {
      const updatedContentTypes = checked
        ? [...prev.contentTypes, contentTypeId]
        : prev.contentTypes.filter(id => id !== contentTypeId);
      
      return {
        ...prev,
        contentTypes: updatedContentTypes
      };
    });
  };

  // 수정 모달용 컨텐츠 타입 체크박스 핸들러
  const handleEditContentTypeChange = (contentTypeId, checked) => {
    setEditingCustomerType(prev => {
      const updatedContentTypes = checked
        ? [...prev.contentTypes, contentTypeId]
        : prev.contentTypes.filter(item => item.id !== contentTypeId.id);

      return {
        ...prev,
        contentTypes: updatedContentTypes
      };
    });
  };

  // 타입 리스트 드래그앤드롭 핸들러
  const handleTypeDragStart = (e, index, group) => {
    setDraggedTypeItem(index);
    setDragGroup(group);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    e.target.style.opacity = '0.5';
  };

  const handleTypeDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTypeItem(null);
    setDragOverTypeItem(null);
    setDragGroup(null);
  };

  const handleTypeDragOver = (e, index, group) => {
    e.preventDefault();
    // 같은 그룹 내에서만 드래그 허용
    if (dragGroup !== group) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOverTypeItem(index);
  };

  const handleTypeDragLeave = () => {
    setDragOverTypeItem(null);
  };

  const handleTypeDrop = (e, dropIndex, group) => {
    e.preventDefault();

    // 같은 그룹 내에서만 드롭 허용
    if (dragGroup !== group || draggedTypeItem === null || draggedTypeItem === dropIndex) {
      setDraggedTypeItem(null);
      setDragOverTypeItem(null);
      setDragGroup(null);
      return;
    }

    // 해당 그룹의 타입들만 필터링
    const groupTypes = customerTypes.filter(type => type.group === group);
    const otherTypes = customerTypes.filter(type => type.group !== group);

    // 순서 변경
    const newGroupTypes = [...groupTypes];
    const draggedType = newGroupTypes[draggedTypeItem];
    newGroupTypes.splice(draggedTypeItem, 1);
    newGroupTypes.splice(dropIndex, 0, draggedType);

    // 전체 리스트 재구성 (기본 그룹을 먼저, 추가 그룹을 나중에)
    const basicTypes = group === 'basic' ? newGroupTypes : customerTypes.filter(t => t.group === 'basic');
    const customTypes = group === 'custom' ? newGroupTypes : customerTypes.filter(t => t.group === 'custom');

    setCustomerTypes([...basicTypes, ...customTypes]);

    setDraggedTypeItem(null);
    setDragOverTypeItem(null);
    setDragGroup(null);
  };

  // 순서 저장 함수
  const handleSaveOrder = async (group) => {
    setIsSavingOrder(true);
    try {
      const groupTypes = customerTypes.filter(type => type.group === group);
      const orderData = groupTypes.map((type, index) => ({
        id: type.id,
        order: index + 1
      }));

      const response = await apiClient.post('/Customer/UpdateCustomerTypeOrder', {
        group: group,
        orders: orderData
      });

      if (response.success) {
        alert('순서가 저장되었습니다.');
      } else {
        alert(response.message || '순서 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('순서 저장 실패:', error);
      alert('순서 저장에 실패했습니다.');
    } finally {
      setIsSavingOrder(false);
    }
  };

  // 컨텐츠 타입 이름 가져오기
  const getContentTypeName = (contentTypeId) => {
    // contentTypeId가 객체인 경우 (직접 name 속성이 있는 경우)
    if (typeof contentTypeId === 'object' && contentTypeId.name) {
      return contentTypeId.name;
    }
    
    // contentTypeId가 문자열인 경우 (ID로 옵션에서 찾기)
    const contentType = contentTypeOptions.find(option => option.id === contentTypeId);
    return contentType ? contentType.name : contentTypeId;
  };

  const getContentTypeToHangul = (contentTypeId) => {
    switch(contentTypeId){
      case 'image':
        return '이미지';
      case 'video':
        return '영상';
      case 'review':
        return '리뷰';
      default:
        return contentTypeId;
    }
    return contentTypeId;
  };

  // 고객 타입 삭제
  const handleDeleteCustomerType = async (id) => {
    if (!window.confirm('이 고객 타입을 삭제하시겠습니까?')) {
      return;
    }

    try {
      // TODO: API 엔드포인트로 변경
      // await apiClient.delete(`/Admin/DeleteCustomerType/${id}`);
      
      setCustomerTypes(customerTypes.filter(type => type.id !== id));
      alert('고객 타입이 삭제되었습니다.');
    } catch (error) {
      console.error('고객 타입 삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  // 고객 타입 수정 모달 열기
  const openEditModal = (customerType) => {
    customerType.contentTypes = customerType.contentTypes.map(contentType => ({
      id: contentType.id,
      name: contentType.name,
    }));
    setEditingCustomerType(customerType);
    setIsEditModalOpen(true);
  };

  // 고객 타입 수정 모달 닫기
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCustomerType({ id: '', name: '', group: 'basic', contentTypes: [] });
  };

  // 고객 타입 수정 저장
  const handleUpdateCustomerType = async () => {
    if (!editingCustomerType.name.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }

    if (editingCustomerType.contentTypes.length === 0) {
      alert('최소 하나의 컨텐츠 타입을 선택해주세요.');
      return;
    }

    setIsUpdatingCustomerType(true);
    try {
      // TODO: API 엔드포인트로 변경
      const response = await apiClient.post('/Customer/UpdateCustomerType', {
        id : editingCustomerType.id,
        name : editingCustomerType.name,
        group: editingCustomerType.group,
        contentTypes : editingCustomerType.contentTypes.map(contentType => contentType.id)
      });
      if (response.success) {
        alert('고객 타입이 수정되었습니다.');
        loadCustomerTypes();
        loadContentTypeOptions();
      } else {
        alert(response.message);
      }
      closeEditModal();
    } catch (error) {
      console.error('고객 타입 수정 실패:', error);
      alert('수정에 실패했습니다.');
    } finally {
      setIsUpdatingCustomerType(false);
    }
  };

// 고객 타입 수정 모달 열기
  const openEditContentModal = (contentType) => {
    setEditingContentSetting(contentType);
    setIsEditContentModalOpen(true);
  };

  // 고객 컨텐츠 수정 모달 닫기
  const closeEditContentModal = () => {
    setIsEditContentModalOpen(false);
    setEditingContentSetting({ id: '', icon: '', label: '', contentType: '', description: '' });
  };

  const handleUpdateContentSetting = async () => {
    if (!editingContentSetting.label.trim() || !editingContentSetting.contentType.trim() || !editingContentSetting.description.trim()) {
      alert('모든 필드를 입력해주세요.');
      return;
    }
    
    setIsUpdatingContentSetting(true);
    try {
      // TODO: API 엔드포인트로 변경
      var response = await apiClient.post('/Customer/UpdateContentType', editingContentSetting);
      if (response.success) {
        await loadContentSettings();
        alert('고객 컨텐츠 타입이 수정되었습니다.');
        closeEditContentModal();
      } else {
        alert(response.message);
      }
    }
    catch (error) {
      console.error('컨텐츠 설정 수정 실패:', error);
      alert('수정에 실패했습니다.');
    } finally {
      setIsUpdatingContentSetting(false);
    }
  };

  

  // 컨텐츠 설정 추가
  const handleAddContentSetting = async () => {
    if (!newContentSetting.id.trim() || !newContentSetting.label.trim() || 
        !newContentSetting.contentType.trim() || !newContentSetting.description.trim()) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    // 중복 체크
    if (contentSettings.some(setting => setting.id === newContentSetting.id)) {
      alert('이미 존재하는 ID입니다.');
      return;
    }

    setIsSavingContent(true);
    try {
      // TODO: API 엔드포인트로 변경
      var response = await apiClient.post('/Customer/AddContentType', newContentSetting);

      if(response.success){
        alert('컨텐츠 설정이 추가되었습니다.');
        setNewContentSetting({ id: '', icon: '', label: '', contentType: 'image', description: '' });
        await loadContentSettings();
      } else {
        alert(response.message);
      }
      
    } catch (error) {
      console.error('컨텐츠 설정 추가 실패:', error);
      alert('추가에 실패했습니다.');
    } finally {
      setIsSavingContent(false);
    }
  };

  // 컨텐츠 설정 삭제
  const handleDeleteContentSetting = async (id) => {
    if (!window.confirm('이 컨텐츠 설정을 삭제하시겠습니까?')) {
      return;
    }

    try {
      // TODO: API 엔드포인트로 변경
      // await apiClient.delete(`/Admin/DeleteContentSetting/${id}`);
      
      setContentSettings(contentSettings.filter(setting => setting.id !== id));
      alert('컨텐츠 설정이 삭제되었습니다.');
    } catch (error) {
      console.error('컨텐츠 설정 삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  return (
    <div className="admin-settings">
      {/* 탭 네비게이션 */}
      <div className="settings-tabs">
        <button
          className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          기본설정
        </button>
        <button
          className={`tab-btn ${activeTab === 'customerType' ? 'active' : ''}`}
          onClick={() => setActiveTab('customerType')}
        >
          고객 타입 설정
        </button>
        <button
          className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          고객 컨텐츠 설정
        </button>
      </div>

      {/* 탭 내용 */}
      <div className="settings-content">
        {/* 기본설정 탭 */}
        {activeTab === 'basic' && (
          <div className="settings-section">
            <div className="section-header">
              <h3>기본설정</h3>
              <p>시스템의 기본 설정을 관리합니다.</p>
            </div>
            
            <div className="form-container">
              <div className="form-group">
                <label>공지사항 문구 설정</label>
                <textarea
                  value={basicSettings.noticeMessage}
                  onChange={(e) => setBasicSettings({
                    ...basicSettings,
                    noticeMessage: e.target.value
                  })}
                  placeholder="공지사항 문구를 입력하세요"
                  rows={3}
                />
              </div>

              {/* 알림톡 설정 */}
              <div className="form-group">
                <label>알림톡 설정</label>
                <label className="alimtalk-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={basicSettings.alimtalkEnabled}
                    onChange={(e) => setBasicSettings({
                      ...basicSettings,
                      alimtalkEnabled: e.target.checked
                    })}
                  />
                  <span className="alimtalk-toggle-track"></span>
                  <span className="alimtalk-toggle-label">
                    {basicSettings.alimtalkEnabled ? '사용함' : '사용 안함'}
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>알림톡 아이디</label>
                <input
                  type="text"
                  value={basicSettings.alimtalkId}
                  onChange={(e) => setBasicSettings({
                    ...basicSettings,
                    alimtalkId: e.target.value
                  })}
                  placeholder="알림톡 아이디를 입력하세요"
                  disabled={!basicSettings.alimtalkEnabled}
                />
              </div>

              <div className="form-group">
                <label>알림톡 발송번호</label>
                <input
                  type="text"
                  value={basicSettings.alimtalkSendNumber}
                  onChange={(e) => setBasicSettings({
                    ...basicSettings,
                    alimtalkSendNumber: e.target.value
                  })}
                  placeholder="알림톡 발송번호를 입력하세요"
                  disabled={!basicSettings.alimtalkEnabled}
                />
              </div>

              <div className="form-group">
                <label>알림톡 API Key</label>
                <input
                  type="text"
                  value={basicSettings.alimtalkApiKey}
                  onChange={(e) => setBasicSettings({
                    ...basicSettings,
                    alimtalkApiKey: e.target.value
                  })}
                  placeholder="알림톡 API Key를 입력하세요"
                  disabled={!basicSettings.alimtalkEnabled}
                />
              </div>

              <div className="form-group">
                <label>알림톡 Sender Key</label>
                <input
                  type="text"
                  value={basicSettings.alimtalkSenderKey}
                  onChange={(e) => setBasicSettings({
                    ...basicSettings,
                    alimtalkSenderKey: e.target.value
                  })}
                  placeholder="알림톡 Sender Key를 입력하세요"
                  disabled={!basicSettings.alimtalkEnabled}
                />
              </div>
              
              <div className="form-actions">
                <button
                  className="save-btn"
                  onClick={handleSaveBasicSettings}
                  disabled={isSavingBasic}
                >
                  {isSavingBasic ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 고객 타입 설정 탭 */}
        {activeTab === 'customerType' && (
          <div className="settings-section">
            <div className="section-header">
              <h3>고객 타입 설정</h3>
              <p>고객 타입을 추가하고 관리합니다.</p>
            </div>
            
            {isLoadingCustomerTypes || isLoadingContentTypes ? (
              <div className="loading-state">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
                <p>
                  {isLoadingCustomerTypes && isLoadingContentTypes 
                    ? '데이터를 불러오는 중...'
                    : isLoadingCustomerTypes 
                    ? '고객 타입 데이터를 불러오는 중...'
                    : '컨텐츠 타입 옵션을 불러오는 중...'
                  }
                </p>
              </div>
            ) : (
              <>
                {/* 타입 추가 */}
                <div className="add-form">
                  <h4>타입 추가</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>ID</label>
                      <input
                        type="text"
                        value={newCustomerType.id}
                        onChange={(e) => {
                          // 영문 소문자, 숫자, 하이픈만 허용
                          let filteredValue = e.target.value.replace(/[^a-z0-9-]/g, '');
                          
                          // 첫 글자가 영문 소문자가 아닌 경우 제거
                          if (filteredValue.length > 0 && !/^[a-z]/.test(filteredValue)) {
                            filteredValue = filteredValue.replace(/^[^a-z]+/, '');
                          }
                          
                          setNewCustomerType({
                            ...newCustomerType,
                            id: filteredValue
                          });
                        }}
                        placeholder="타입 ID를 입력하세요 (첫 글자는 영문소문자, 나머지는 영문소문자/숫자/- 가능)"
                        maxLength="50"
                      />
                    </div>
                    <div className="form-group">
                      <label>이름</label>
                      <input
                        type="text"
                        value={newCustomerType.name}
                        onChange={(e) => setNewCustomerType({
                          ...newCustomerType,
                          name: e.target.value
                        })}
                        placeholder="타입 이름을 입력하세요"
                      />
                    </div>
                    <div className="form-group">
                      <label>그룹 설정</label>
                      <select
                    value={newCustomerType.group}
                    onChange={(e) => setNewCustomerType({
                      ...newCustomerType,
                      group: e.target.value
                    })}
                  >
                    <option value="basic">기본</option>
                      <option value="custom">추가</option>
                  </select>
                    </div>
                  </div>
                  
                  {/* 컨텐츠 타입 선택 */}
                  <div className="content-type-selection">
                    <label className="content-type-label">컨텐츠 타입</label>
                    <div className="checkbox-group">
                      {contentTypeOptions.map((option) => (
                        <div key={option.id} className="checkbox-item">
                          <input
                            type="checkbox"
                            id={`content-type-${option.id}`}
                            checked={newCustomerType.contentTypes.includes(option.id)}
                            onChange={(e) => handleContentTypeChange(option.id, e.target.checked)}
                          />
                          <label htmlFor={`content-type-${option.id}`} className="checkbox-label">
                            {option.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* 추가 버튼 */}
                  <div className="add-button-container">
                    <button
                      className="add-btn"
                      onClick={handleAddCustomerType}
                      disabled={isSavingCustomerType}
                    >
                      {isSavingCustomerType ? '추가 중...' : '추가'}
                    </button>
                  </div>
                </div>

                {/* 기본 그룹 타입 리스트 */}
                <div className="list-container">
                  <div className="list-header">
                    <h4>기본 타입</h4>
                    <button
                      className="save-order-btn"
                      onClick={() => handleSaveOrder('basic')}
                      disabled={isSavingOrder}
                    >
                      {isSavingOrder ? '저장 중...' : '순서 저장'}
                    </button>
                  </div>
                  <p className="list-description">드래그하여 순서를 변경할 수 있습니다.</p>
                  <div className="sortable-type-list">
                    {customerTypes.filter(type => type.group === 'basic').length > 0 ? (
                      customerTypes
                        .filter(type => type.group === 'basic')
                        .map((type, index) => (
                          <div
                            key={type.id}
                            className={`sortable-type-item ${dragOverTypeItem === index && dragGroup === 'basic' ? 'drag-over' : ''} ${draggedTypeItem === index && dragGroup === 'basic' ? 'dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleTypeDragStart(e, index, 'basic')}
                            onDragEnd={handleTypeDragEnd}
                            onDragOver={(e) => handleTypeDragOver(e, index, 'basic')}
                            onDragLeave={handleTypeDragLeave}
                            onDrop={(e) => handleTypeDrop(e, index, 'basic')}
                          >
                            <span className="drag-handle">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="9" cy="5" r="1"/>
                                <circle cx="9" cy="12" r="1"/>
                                <circle cx="9" cy="19" r="1"/>
                                <circle cx="15" cy="5" r="1"/>
                                <circle cx="15" cy="12" r="1"/>
                                <circle cx="15" cy="19" r="1"/>
                              </svg>
                            </span>
                            <span className="type-order">{index + 1}</span>
                            <div className="type-info">
                              <span className="type-id">{type.id}</span>
                              <span className="type-name">{type.name}</span>
                            </div>
                            <div className="content-type-tags">
                              {type.contentTypes?.map((contentTypeId) => (
                                <span key={type.id + "_" + contentTypeId.id} className="content-type-tag">
                                  {getContentTypeName(contentTypeId)}
                                </span>
                              )) || '-'}
                            </div>
                            <button
                              className="table-action-btn edit-btn"
                              onClick={() => openEditModal(type)}
                              title="수정"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                          </div>
                        ))
                    ) : (
                      <div className="no-data">등록된 기본 타입이 없습니다.</div>
                    )}
                  </div>
                </div>

                {/* 추가 그룹 타입 리스트 */}
                <div className="list-container">
                  <div className="list-header">
                    <h4>추가 타입</h4>
                    <button
                      className="save-order-btn"
                      onClick={() => handleSaveOrder('custom')}
                      disabled={isSavingOrder}
                    >
                      {isSavingOrder ? '저장 중...' : '순서 저장'}
                    </button>
                  </div>
                  <p className="list-description">드래그하여 순서를 변경할 수 있습니다.</p>
                  <div className="sortable-type-list">
                    {customerTypes.filter(type => type.group === 'custom').length > 0 ? (
                      customerTypes
                        .filter(type => type.group === 'custom')
                        .map((type, index) => (
                          <div
                            key={type.id}
                            className={`sortable-type-item ${dragOverTypeItem === index && dragGroup === 'custom' ? 'drag-over' : ''} ${draggedTypeItem === index && dragGroup === 'custom' ? 'dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleTypeDragStart(e, index, 'custom')}
                            onDragEnd={handleTypeDragEnd}
                            onDragOver={(e) => handleTypeDragOver(e, index, 'custom')}
                            onDragLeave={handleTypeDragLeave}
                            onDrop={(e) => handleTypeDrop(e, index, 'custom')}
                          >
                            <span className="drag-handle">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="9" cy="5" r="1"/>
                                <circle cx="9" cy="12" r="1"/>
                                <circle cx="9" cy="19" r="1"/>
                                <circle cx="15" cy="5" r="1"/>
                                <circle cx="15" cy="12" r="1"/>
                                <circle cx="15" cy="19" r="1"/>
                              </svg>
                            </span>
                            <span className="type-order">{index + 1}</span>
                            <div className="type-info">
                              <span className="type-id">{type.id}</span>
                              <span className="type-name">{type.name}</span>
                            </div>
                            <div className="content-type-tags">
                              {type.contentTypes?.map((contentTypeId) => (
                                <span key={type.id + "_" + contentTypeId.id} className="content-type-tag">
                                  {getContentTypeName(contentTypeId)}
                                </span>
                              )) || '-'}
                            </div>
                            <button
                              className="table-action-btn edit-btn"
                              onClick={() => openEditModal(type)}
                              title="수정"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                          </div>
                        ))
                    ) : (
                      <div className="no-data">등록된 추가 타입이 없습니다.</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* 고객 컨텐츠 설정 탭 */}
        {activeTab === 'content' && (
          <div className="settings-section">
            <div className="section-header">
              <h3>고객 컨텐츠 설정</h3>
              <p>고객 컨텐츠 항목을 추가하고 관리합니다.</p>
            </div>
            
            {/* 컨텐츠 항목 추가 */}
            <div className="add-form">
              <h4>컨텐츠 항목 추가</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="text"
                    value={newContentSetting.id}
                    onChange={(e) => {
                      // 영문 소문자, 숫자, 하이픈만 허용
                      let filteredValue = e.target.value.replace(/[^a-z0-9-]/g, '');
                      
                      // 첫 글자가 영문 소문자가 아닌 경우 제거
                      if (filteredValue.length > 0 && !/^[a-z]/.test(filteredValue)) {
                        filteredValue = filteredValue.replace(/^[^a-z]+/, '');
                      }
                      
                      setNewContentSetting({
                        ...newContentSetting,
                        id: filteredValue
                      });
                    }}
                    placeholder="컨텐츠 ID를 입력하세요 (첫 글자는 영문소문자, 나머지는 영문소문자/숫자/- 가능)"
                    maxLength="50"
                  />
                </div>
                <div className="form-group">
                  <label>아이콘 (SVG)</label>
                  <textarea
                    value={newContentSetting.icon}
                    onChange={(e) => setNewContentSetting({
                      ...newContentSetting,
                      icon: e.target.value
                    })}
                    placeholder="SVG 태그를 입력하세요"
                    rows={2}
                  />
                </div>
                <div className="form-group">
                  <label>컨텐츠 명</label>
                  <input
                    type="text"
                    value={newContentSetting.label}
                    onChange={(e) => setNewContentSetting({
                      ...newContentSetting,
                      label: e.target.value
                    })}
                    placeholder="컨텐츠 명을 입력하세요"
                  />
                </div>
                <div className="form-group">
                  <label>컨텐츠 타입</label>
                  <select
                    value={newContentSetting.contentType}
                    onChange={(e) => setNewContentSetting({
                      ...newContentSetting,
                      contentType: e.target.value
                    })}
                  >
                    <option value="image">이미지</option>
                      <option value="review">리뷰</option>
                      <option value="video">영상</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>컨텐츠 설명</label>
                  <textarea
                    value={newContentSetting.description}
                    onChange={(e) => setNewContentSetting({
                      ...newContentSetting,
                      description: e.target.value
                    })}
                    placeholder="컨텐츠 설명을 입력하세요"
                    rows={2}
                  />
                </div>
                <div className="form-group full-width">
                  <button
                    className="add-btn"
                    onClick={handleAddContentSetting}
                    disabled={isSavingContent}
                  >
                    {isSavingContent ? '추가 중...' : '추가'}
                  </button>
                </div>
              </div>
            </div>

            {/* 컨텐츠 리스트 */}
            <div className="list-container">
              <h4>컨텐츠 리스트</h4>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>아이콘</th>
                      <th>컨텐츠 명</th>
                      <th>컨텐츠 타입</th>
                      <th>컨텐츠 설명</th>
                      <th className="action-col">수정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contentSettings.length > 0 ? (
                      contentSettings.map((setting) => (
                        <tr key={setting.id}>
                          <td>{setting.id}</td>
                          <td>
                            <div className="icon-preview" dangerouslySetInnerHTML={{ __html: setting.icon }} />
                          </td>
                          <td>{setting.label}</td>
                          <td>{getContentTypeToHangul(setting.contentType)}</td>
                          <td>{setting.description}</td>
                          <td>
                            <button
                              className="table-action-btn edit-btn"
                              onClick={() => openEditContentModal(setting)}
                              title="수정"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="no-data">등록된 컨텐츠 설정이 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 고객 타입 수정 모달 */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>고객 타입 수정</h3>
              <button 
                className="close-btn"
                onClick={closeEditModal}
                title="닫기"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18"/>
                  <path d="M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>이름</label>
                <input
                  type="text"
                  value={editingCustomerType.name}
                  onChange={(e) => setEditingCustomerType({
                    ...editingCustomerType,
                    name: e.target.value
                  })}
                  placeholder="타입 이름을 입력하세요"
                />
              </div>
              <div className="form-group">
                <label>그룹</label>
                <select
                  value={editingCustomerType.group}
                  onChange={(e) => setEditingCustomerType({
                    ...editingCustomerType,
                    group: e.target.value
                  })}
                >
                  <option value="basic">기본</option>
                  <option value="custom">추가</option>
                </select>
              </div>
              <div className="content-type-selection">
                <label className="content-type-label">컨텐츠 타입</label>
                <div className="checkbox-group">
                  {contentTypeOptions.map((option) => (
                    <div key={option.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        id={`edit-content-type-${option.id}`}
                        checked={editingCustomerType.contentTypes.find(type => type.id === option.id) ? true : false}
                        onChange={(e) => handleEditContentTypeChange(option, e.target.checked)}
                      />
                      <label htmlFor={`edit-content-type-${option.id}`} className="checkbox-label">
                        {option.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn cancel-btn"
                onClick={closeEditModal}
              >
                취소
              </button>
              <button
                className="modal-btn save-btn"
                onClick={handleUpdateCustomerType}
                disabled={isUpdatingCustomerType}
              >
                {isUpdatingCustomerType ? '수정 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 고객 컨텐츠 수정 모달 */}
      {isEditContentModalOpen && (
        <div className="modal-overlay" onClick={closeEditContentModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>고객 컨텐츠 수정</h3>
              <button 
                className="close-btn"
                onClick={closeEditContentModal}
                title="닫기"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18"/>
                  <path d="M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>컨텐츠 명</label>
                <input
                  type="text"
                  value={editingContentSetting.label}
                  onChange={(e) => setEditingContentSetting({
                    ...editingContentSetting,
                    label: e.target.value
                  })}
                  placeholder="컨텐츠 명을 입력하세요"
                />
              </div>
              <div className="form-group">
                <label>컨텐츠 타입</label>
                <select
                  value={editingContentSetting.contentType}
                  onChange={(e) => setEditingContentSetting({
                    ...editingContentSetting,
                    contentType: e.target.value
                  })}
                >
                  <option value="image">이미지</option>
                  <option value="review">리뷰</option>
                  <option value="video">영상</option>
                </select>
              </div>
              <div className="form-group">
                <label>컨텐츠 설명</label>
                <textarea
                  value={editingContentSetting.description}
                  onChange={(e) => setEditingContentSetting({
                    ...editingContentSetting,
                    description: e.target.value
                  })}
                  placeholder="컨텐츠 설명을 입력하세요"
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>아이콘 (SVG)</label>
                <textarea
                  value={editingContentSetting.icon}
                  onChange={(e) => setEditingContentSetting({
                    ...editingContentSetting,
                    icon: e.target.value
                  })}
                  placeholder="SVG 태그를 입력하세요"
                  rows={2}
                />
              </div>

            </div>
            <div className="modal-actions">
              <button
                className="modal-btn cancel-btn"
                onClick={closeEditContentModal}
              >
                취소
              </button>
              <button
                className="modal-btn save-btn"
                onClick={handleUpdateContentSetting}
                disabled={isUpdatingContentSetting}
              >
                {isUpdatingContentSetting ? '수정 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;